/**
 * Flujo de demo del widget **corriendo sobre el motor real** (modo mock):
 * usa reservationEngine + paymentEngine de @shared con clientes mock. Así el
 * bot que ves ejecuta la MISMA máquina de estados que irá a producción — solo
 * cambia el cliente (mock ↔ live). Ver docs/architecture/MAQUINA-DE-ESTADOS.md.
 */
import {
  MockCarryonClient,
  type RoomOption,
  type SearchCriteria,
  type Holder,
  type Occupant,
} from '@shared/carryon'
import { MockPaymentClient } from '@shared/payments'
import {
  createReservation,
  runSearch,
  selectRoom,
  revalidate,
  capturePassengers,
  book,
} from '@shared/reservationEngine'
import { requirePayment, authorizeCard, captureCard, voidCard } from '@shared/paymentEngine'
import type { Reservation } from '@shared/types'
import type { UiMessage } from '@/types'

const CRITERIA: SearchCriteria = {
  placeId: 'place-cun', placeLabel: 'Cancún',
  arrival: '2026-05-01', departure: '2026-05-05', adults: 2,
}
const HOLDER: Holder = { firstName: 'María', lastName: 'González' }
const OCCUPANTS: Occupant[] = [{ name: 'María', lastName: 'González', type: 'adult', age: 34 }]

const MEAL: Record<string, string> = {
  AI: 'Todo incluido', BB: 'Desayuno', HB: 'Media pensión',
  FB: 'Pensión completa', RO: 'Solo habitación', ZZ: 'Solo habitación',
}

function uid(): string {
  return crypto.randomUUID()
}
function botText(text: string): UiMessage {
  return { id: uid(), kind: 'text', text, senderType: 'bot', senderName: 'Asistente', generatedByAi: true }
}
export function greeting(): UiMessage {
  return botText(
    '¡Hola! Soy tu asistente de viajes ✦. Te ayudo a buscar y reservar hoteles aquí mismo. Escribe "reservar" para ver el flujo.',
  )
}

export interface DemoEngine {
  onText(text: string): Promise<UiMessage[]>
  onAction(token: string): Promise<UiMessage[]>
}

/** Crea un motor de demo con estado propio (una "conversación"). */
export function createDemoEngine(): DemoEngine {
  const carryon = new MockCarryonClient()
  const pay = new MockPaymentClient()
  let reservation: Reservation | null = null
  let options: { hotelName: string; room: RoomOption }[] = []
  let selected: { hotelName: string; room: RoomOption } | null = null

  async function onText(text: string): Promise<UiMessage[]> {
    const t = text.toLowerCase()
    if (/hola|buenas|hey|noche|d[ií]as|tarde/.test(t)) return [greeting()]

    if (/reservar|hotel|canc[uú]n|playa|viaj/.test(t)) {
      reservation = createReservation('bot')
      const { res, hotels } = await runSearch(carryon, reservation, CRITERIA)
      reservation = res
      options = hotels.flatMap((h) => h.rooms.map((room) => ({ hotelName: h.name, room })))
      const cards: UiMessage[] = options.map((o) => ({
        id: uid(), kind: 'hotel_card', text: `${o.hotelName} — ${o.room.name}`, senderType: 'bot',
        hotelCard: {
          hotelName: o.hotelName, roomName: o.room.name, price: o.room.price, currency: o.room.currency,
          refundable: o.room.refundable, mealPlan: MEAL[o.room.mealPlan] ?? o.room.mealPlan,
          rateKeyRef: o.room.rateKey.rateKey,
        },
      }))
      return [
        botText('Perfecto ✦. Te muestro opciones en Cancún (2 adultos, 4 noches):'),
        ...cards,
        botText('Toca "Elegir" en la que prefieras para continuar al pago.'),
      ]
    }

    return [botText('Puedo ayudarte a buscar y reservar hoteles. Escribe "reservar" para ver el flujo completo. ✦')]
  }

  async function onAction(token: string): Promise<UiMessage[]> {
    // El modo demo no arma carrito: elegir lleva directo al pago. El carrito lo
    // publica el backend (`reservationSelect`), que aquí no existe — y una
    // demostración sin llaves no debería depender de él para verse completa.
    // Elegir habitación → revalidar tarifa → capturar pasajeros → pedir pago
    if (token.startsWith('select:') && reservation) {
      const key = token.slice('select:'.length)
      const opt = options.find((o) => o.room.rateKey.rateKey === key)
      if (!opt) return [botText('Esa opción ya no está disponible, intenta de nuevo.')]
      selected = opt
      reservation = selectRoom(reservation, opt.room)
      reservation = await revalidate(carryon, reservation, opt.room)
      if (reservation.status === 'requote_requerido') {
        reservation = await revalidate(carryon, reservation, opt.room)
      }
      reservation = await capturePassengers(carryon, reservation, opt.room, HOLDER, OCCUPANTS)
      reservation = requirePayment(reservation)
      return [
        botText('Excelente elección. Aquí tu pago seguro — si la reserva no se confirma, no te quedas con el cargo:'),
        {
          id: uid(), kind: 'payment', text: 'Pago', senderType: 'bot',
          payment: { rail: 'tarjeta', amount: reservation.price ?? 0, currency: reservation.currency ?? 'MXN', status: 'pago_requerido' },
        },
      ]
    }

    // Pagar → autorizar → reservar → capturar (o anular si falla)
    if (token === 'pay' && reservation && selected) {
      reservation = await authorizeCard(pay, reservation, `idem-${reservation.id}`)
      if (reservation.paymentStatus === 'rechazado') {
        return [botText('El pago fue rechazado. ¿Intentamos con otra tarjeta?')]
      }
      reservation = await book(carryon, reservation, selected.room)
      if (reservation.status !== 'confirmada') {
        reservation = await voidCard(pay, reservation)
        return [botText('No se pudo confirmar la reserva; liberamos la retención (no se te cobró). Un asesor te contactará.')]
      }
      reservation = await captureCard(pay, reservation)
      return [
        botText('¡Reserva confirmada! 🎉 Aquí tu comprobante:'),
        {
          id: uid(), kind: 'booking_summary', text: 'Reserva confirmada', senderType: 'bot',
          bookingSummary: {
            reservationId: reservation.id, locator: reservation.locator ?? null,
            hotelName: selected.hotelName, checkIn: CRITERIA.arrival, checkOut: CRITERIA.departure,
            total: reservation.price ?? 0, currency: reservation.currency ?? 'MXN',
          },
        },
        botText('¿Te ayudo con algo más? 🙂'),
      ]
    }

    return [botText('Listo.')]
  }

  return { onText, onAction }
}
